import React, { useEffect, useRef } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export function Modal({ isOpen, onClose, title, children }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const titleId = 'modal-title';

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;

    const active = document.activeElement as HTMLElement | null;
    if (!active || !panel.contains(active)) {
      const candidates = panel.querySelectorAll<HTMLElement>(
        'input:not([type="hidden"]), textarea, select, [autofocus], [tabindex]:not([tabindex="-1"]), button, [href]'
      );
      const list = Array.from(candidates).filter(
        el => !el.hasAttribute('disabled') && !el.hasAttribute('data-modal-close')
      );
      list[0]?.focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
      if (e.key === 'Tab') {
        const focusables = panel.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const list = Array.from(focusables).filter(el => !el.hasAttribute('disabled'));
        if (list.length === 0) return;
        const first = list[0];
        const last = list[list.length - 1];
        const activeEl = document.activeElement as HTMLElement | null;
        if (!activeEl) return;
        if (!panel.contains(activeEl)) {
          first.focus();
          e.preventDefault();
          return;
        }
        if (e.shiftKey && activeEl === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && activeEl === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col justify-end sm:items-center sm:justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-500/75"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet / panel */}
      <div
        ref={panelRef}
        className="relative w-full bg-white rounded-t-2xl shadow-xl sm:rounded-lg sm:max-w-4xl sm:w-full sm:my-8 flex flex-col max-h-[90vh]"
      >
        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 id={titleId} className="text-base font-semibold text-gray-900">
            {title}
          </h3>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700 p-1.5 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close"
            data-modal-close
            onClick={onClose}
          >
            <span aria-hidden className="text-xl leading-none">×</span>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5" style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom, 0px))' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
